import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ReferenceBookApiService, ReferenceBookResponse} from '@npck/af-frontend-api';
import {RouterService, rxSpinner, Spinner} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {EMPTY, mergeMap, Observable, Subscription} from 'rxjs';
import {DeleteDialogComponent, DeleteDialogData} from '../../../components/delete-dialog/delete-dialog.component';
import {
    Column,
    ColumnType,
    CustomLayoutColumn,
    ELLIPSIS_LARGE_TEXT_MAX_LENGTH,
    Row,
} from '../../../components/full-table/table/table.component';
import {DictionaryHolderService} from '../../../domain/dictionary-holder.service';
import {DICTIONARY_ITEM_ID, DICTIONARY_NAME} from '../../../domain/query-params.keys';
import {Path} from '../../../path';
import {AdminPanelTab} from '../admin.component';

@Component({
    selector: 'af-dictionary',
    templateUrl: 'dictionary.component.html',
    standalone: false
})
export class DictionaryComponent implements OnInit, OnDestroy {
    @Input() buttonClickStream: Observable<AdminPanelTab>;
    @Output() changeButtonText = new EventEmitter<string>();
    Spinner = Spinner;
    form: FormGroup<ReferenceForm>;
    dataSource: Row[] = [];
    fields: (keyof ReferenceBookResponse)[] = ['id', 'name_kz', 'name_ru', 'name_en', 'code', 'code1', 'code2', 'type'];
    dictionariesSubscription: Subscription;
    dictionarySubscription: Subscription;
    buttonClickSubscription: Subscription;
    isDestroyed = false;

    private selectedDictionary = 'reference';

    constructor(
        private formBuilder: FormBuilder,
        private routerService: RouterService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private dictionaryHolderService: DictionaryHolderService,
        private referenceBookApiService: ReferenceBookApiService,
    ) {
        this.form = this.formBuilder.group({dictionaryType: new FormControl(null, [Validators.required])});
    }

    ngOnInit(): void {
        this.dictionarySubscription = this.form.controls.dictionaryType.valueChanges
            .pipe(mergeMap(reference => {
                this.selectedDictionary = reference;
                this.dataSource = [];
                return this.dictionaryHolderService.getReference(reference).pipe(rxSpinner('filter'));
            }))
            .subscribe(reference => this.buildDataSource(reference));
        this.buttonClickSubscription = this.buttonClickStream?.subscribe(activeTab => {
            this.routerService.navigateToTab<AdminPanelTab>(
                Path.ADMIN_PANEL_DICTIONARY_ADD_VALUE.replace(`:${DICTIONARY_NAME}`, this.selectedDictionary),
                activeTab,
            );
        });
        this.changeButtonText.emit('i18n_af_buttonAddDictionaryValue');
    }

    ngOnDestroy(): void {
        this.dictionarySubscription?.unsubscribe();
        this.dictionariesSubscription?.unsubscribe();
        this.buttonClickSubscription?.unsubscribe();
        this.isDestroyed = true;
    }

    private buildDataSource(response: ReferenceBookResponse[]): void {
        this.dataSource = response.map(reference => {
                const row = <Row>{
                    actions: <CustomLayoutColumn>{
                        type: ColumnType.CUSTOM_LAYOUT,
                        cellStyle: {
                            layoutDirection: 'row',
                            layoutAlignFirst: 'start',
                            layoutAlignSecond: 'center',
                            layoutGap: '8px',
                        },
                        layoutContentItems: [
                            {
                                type: 'icon',
                                text: 'ic_edit',
                                click: () => this.editReferenceValue(reference.id),
                                tooltip: 'i18n_af_labelEdit',
                                iconSize: 'i-16',
                            },
                            {
                                type: 'icon',
                                text: 'ic_delete',
                                click: () => this.deleteReferenceValue(reference),
                                tooltip: 'i18n_af_labelDeactivate',
                                iconSize: 'i-16',
                            },
                        ],
                    },
                };
                return this.fields
                    .map(field => <ReferenceField>{
                        key: field === 'id' && field.toUpperCase() || field,
                        value: reference[field],
                    })
                    .reduce((acc, item) => {
                        let column = <Column>{
                            type: ColumnType.TEXT,
                            text: item.value || '-',
                        };
                        if (item.key !== 'id' && column.text.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH) {
                            column = {
                                ...column,
                                cellStyle: {ellipsisTextLarge: true},
                                tooltip: {text: column.text},
                            };
                        }
                        acc[item.key] = column;
                        return acc;
                    }, row);
            },
        );
    }

    private reloadDictionary(): void {
        this.dictionaryHolderService.getReference(this.selectedDictionary)
            .subscribe(references => this.buildDataSource(references));
    }

    private editReferenceValue(code: string): void {
        this.routerService.navigateToTab<AdminPanelTab>(
            Path.ADMIN_PANEL_DICTIONARY_EDIT_VALUE
                .replace(`:${DICTIONARY_NAME}`, this.selectedDictionary)
                .replace(`:${DICTIONARY_ITEM_ID}`, code),
            'tabUsers');
    }

    private deleteReferenceValue(item: ReferenceBookResponse): void {
        this.dialogService.openCustomDialog<DeleteDialogComponent, ReferenceDeletionReason>(DeleteDialogComponent, <DeleteDialogData>{
            id: item.id,
            title: 'i18n_af_dictionaryItemDeletionDialogTitle',
            subtitle: 'i18n_af_dictionaryItemDeletionDialogSubtitle',
        })

            .pipe(
                mergeMap(deletionReason => !deletionReason && EMPTY || this.referenceBookApiService.updateReferenceBookValue(item.id, {
                    ...item,
                    isDeleted: true,
                    deletionReason: deletionReason.comment,
                })
                    .pipe(rxSpinner('deleting'))),
            )
            .subscribe((response) => {
                this.dictionaryHolderService.addOrUpdateExistingCachedValue(response);
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageDictionaryItemDeleted', 'success', false, {id: item.id});
                this.reloadDictionary();
            });
    }
}

interface ReferenceField {
    key: keyof ReferenceBookResponse;
    value: string;
}

interface ReferenceDeletionReason {
    comment: string;
    reason: string | null;
}

export interface ReferenceForm {
    dictionaryType: FormControl<string>;
}
